/**
 * CUSTOMER-172A / CUSTOMER-R4.15 — typing suggest via Worker/Pages search API.
 * NEVER builds/scans full catalog on the input frame.
 */
import type { SearchSuggestion } from "./search-v2.js";
import { hitsToSuggestions, serverSuggest } from "./search-server-client-v2.js";

export type SuggestRequest = {
  seq: number;
  query: string;
  limit: number;
};

export type SuggestResponse = {
  seq: number;
  rows: SearchSuggestion[];
  computeMs: number;
  engine: "server_suggest_v1";
  /** True when network/API failed — callers must not treat as intentional empty. */
  failed?: boolean;
};

type Listener = (res: SuggestResponse) => void;

/** @deprecated compact index no longer used on device — kept for guard compat */
export function setSuggestCompactIndex(_index: unknown): void {
  /* no-op: server search */
}

export function getSuggestCompactIndex(): null {
  return null;
}

/**
 * Schedules server suggest AFTER debounce caller — never sync on keystroke.
 * Products snapshot ignored.
 */
export function scheduleSuggest(
  _productsIgnored: unknown,
  req: SuggestRequest,
  onDone: Listener,
): () => void {
  let cancelled = false;
  // Never reuse AbortController after abort() — new instance per schedule.
  const ac = typeof AbortController !== "undefined" ? new AbortController() : null;

  const run = () => {
    if (cancelled) return;
    const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
    void serverSuggest(req.query, req.limit, ac?.signal)
      .then((res) => {
        if (cancelled) return;
        const computeMs =
          (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
        // CUSTOMER-R4.15 — SEARCH_ERROR_SEMANTICS: do not pretend success-empty on fail.
        if (!res.ok) {
          onDone({ seq: req.seq, rows: [], computeMs, engine: "server_suggest_v1", failed: true });
          return;
        }
        const rows = hitsToSuggestions(res.hits ?? []);
        onDone({ seq: req.seq, rows, computeMs, engine: "server_suggest_v1", failed: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const name = err && typeof err === "object" && "name" in err ? String((err as { name: string }).name) : "";
        if (name === "AbortError") return;
        onDone({ seq: req.seq, rows: [], computeMs: 0, engine: "server_suggest_v1", failed: true });
      });
  };

  // Microtask / timeout — never sync on input frame.
  const timeoutId = setTimeout(run, 0);

  return () => {
    cancelled = true;
    clearTimeout(timeoutId);
    ac?.abort();
  };
}
